'use client';

import { c as _c } from "react/compiler-runtime";
import { type InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { memo, Suspense, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { getCarouselsAction } from '@module/mdrt-org/shared/block-components/learn-carousel-block/actions/get-carousels-action';
import { type GetCarouselsActionResponse } from '@module/mdrt-org/shared/block-components/learn-carousel-block/actions/types';
import { LearnContentCarouselSkeleton } from '@module/mdrt-org/shared/block-components/learn-carousel-block/components/learn-content-carousel/components/learn-content-carousel-skeleton';
import { LearnContentCarousel, type LearnContentCarouselProps } from '@module/mdrt-org/shared/block-components/learn-carousel-block/components/learn-content-carousel/learn-content-carousel';
import { CAROUSELS_PER_PAGE, LearnDataTag } from '@module/mdrt-org/shared/block-components/learn-carousel-block/constants';
import { type InterestsFormModalCmsContent } from '@module/mdrt-org/shared/utils/types/interests-form-modal-cms-content';
import { mapIntlLocaleToOptimizelyLocale } from '@shared/utils/helpers/map-intl-locale-to-optimizely-locale';
import { type LearnCarouselBlockFragment, type PageMainContentFragment } from '../../utils/data/graphql/_generated/gql-generated';
import { type InterestsData } from '../../utils/data/graphql/utils/get-topics';
import { type LearnGenericTextsCmsContent } from './graphql/get-learn-generic-texts-cms-content';
import { LearnCarouselContentPageSkeleton } from './learn-carousel-content-page-skeleton';
import { getPageCarouselBlockGuids } from './utils/get-page-carousel-block-guids';
const LearnContentCarouselWrapper = memo(props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <LearnContentCarouselSkeleton />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Suspense fallback={t0}><LearnContentCarousel {...props} /></Suspense>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}, (previous, next) => previous.carouselGuid === next.carouselGuid);
const getQueryKeyOfGetCarouselsAction = (pageBlocks: PageMainContentFragment[]) => {
  return [LearnDataTag.GET_CAROUSELS_ACTION, pageBlocks];
};
export type LearnCarouselBlockClientProps = {
  cmsContent: LearnGenericTextsCmsContent;
  currentBlock: LearnCarouselBlockFragment;
  customerInterests: number[];
  initialGetCarouselsResponse: GetCarouselsActionResponse;
  pageBlocks: PageMainContentFragment[];
  preferencesCmsContent: InterestsFormModalCmsContent;
  preferenceTopics: InterestsData;
};
const LearnCarouselBlockClientInner = t0 => {
  const $ = _c(38);
  const {
    cmsContent,
    currentBlock,
    customerInterests,
    initialGetCarouselsResponse,
    pageBlocks,
    preferencesCmsContent,
    preferenceTopics
  } = t0;
  const locale = useLocale();
  const pageCarouselBlockGuids = getPageCarouselBlockGuids(pageBlocks);
  const {
    data,
    fetchNextPage,
    isError,
    isFetchingNextPage
  } = useInfiniteQuery({
    enabled: false,
    getNextPageParam: _temp,
    initialPageParam: initialGetCarouselsResponse.nextCategoriesToSkip,
    queryFn: t1 => {
      const {
        pageParam
      } = t1;
      return getCarouselsAction({
        Categories: pageCarouselBlockGuids.map(_temp2),
        CategoriesToSkip: pageParam,
        Language: mapIntlLocaleToOptimizelyLocale(locale),
        Limit: CAROUSELS_PER_PAGE
      });
    },
    queryKey: getQueryKeyOfGetCarouselsAction(pageBlocks)
  });
  let t2;
  if ($[0] !== data?.pages) {
    t2 = data?.pages ?? [];
    $[0] = data?.pages;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== initialGetCarouselsResponse || $[3] !== t2) {
    t3 = [initialGetCarouselsResponse, ...t2];
    $[2] = initialGetCarouselsResponse;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const carouselsPages = t3;
  let t4;
  if ($[5] !== carouselsPages || $[6] !== currentBlock.ContentLink?.GuidValue) {
    t4 = carouselsPages.flatMap(carouselsPage => carouselsPage.guidToCarousels?.[currentBlock?.ContentLink?.GuidValue ?? ""] ?? []);
    $[5] = carouselsPages;
    $[6] = currentBlock.ContentLink?.GuidValue;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const carouselsForCurrentBlock = t4;
  let t5;
  if ($[8] !== carouselsPages) {
    t5 = carouselsPages.flatMap(_temp3);
    $[8] = carouselsPages;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const loadedCarouselGuids = t5;
  let t6;
  if ($[10] !== loadedCarouselGuids) {
    t6 = pageCarouselGuid => loadedCarouselGuids.includes(pageCarouselGuid);
    $[10] = loadedCarouselGuids;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const loadedPageCarouselGuids = pageCarouselBlockGuids.filter(t6);
  const isCurrentCarouselLastLoaded = loadedPageCarouselGuids.toReversed().indexOf(currentBlock?.ContentLink?.GuidValue ?? "") === 0;
  let t7;
  if ($[12] !== carouselsPages || $[13] !== isError) {
    t7 = isError || carouselsPages.length > 0 && Object.entries(carouselsPages[carouselsPages.length - 1]?.guidToCarousels ?? {}).length === 0;
    $[12] = carouselsPages;
    $[13] = isError;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const finishedFetching = t7;
  const isFirstCarousel = pageCarouselBlockGuids[0] === currentBlock.ContentLink?.GuidValue;
  const showLoaderAtTheEnd = (isCurrentCarouselLastLoaded || isFirstCarousel && carouselsPages.length === 0) && !finishedFetching;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      rootMargin: "600px",
      threshold: 0
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const {
    inView,
    ref: inViewRef
  } = useInView(t8);
  let t10;
  let t9;
  if ($[16] !== fetchNextPage || $[17] !== finishedFetching || $[18] !== inView || $[19] !== isCurrentCarouselLastLoaded || $[20] !== isFetchingNextPage) {
    t9 = () => {
      if (inView && isCurrentCarouselLastLoaded && !finishedFetching && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    t10 = [fetchNextPage, finishedFetching, inView, isCurrentCarouselLastLoaded, isFetchingNextPage];
    $[16] = fetchNextPage;
    $[17] = finishedFetching;
    $[18] = inView;
    $[19] = isCurrentCarouselLastLoaded;
    $[20] = isFetchingNextPage;
    $[21] = t10;
    $[22] = t9;
  } else {
    t10 = $[21];
    t9 = $[22];
  }
  useEffect(t9, t10);
  let t11;
  if ($[23] !== carouselsForCurrentBlock || $[24] !== cmsContent || $[25] !== customerInterests || $[26] !== preferenceTopics || $[27] !== preferencesCmsContent) {
    t11 = carouselsForCurrentBlock.length > 0 && carouselsForCurrentBlock.map(carousel => <LearnContentCarouselWrapper cards={carousel.cards} carouselGuid={carousel.guid} carouselType={carousel.type} cmsContent={cmsContent} ctaLink={carousel.ctaLink} customerInterests={customerInterests} iconSrc={carousel.iconSrc} key={carousel.guid} preferencesCmsContent={preferencesCmsContent} preferenceTopics={preferenceTopics} selectedSourcesIds={carousel.selectedSourcesIds} showMagazineIssueFilter={carousel.showMagazineIssueFilter} showSourcesFilter={carousel.showSourcesFilter} sourcesFilterAllSelectionLabel={carousel.sourcesFilterAllSelectionLabel} title={carousel.title} />);
    $[23] = carouselsForCurrentBlock;
    $[24] = cmsContent;
    $[25] = customerInterests;
    $[26] = preferenceTopics;
    $[27] = preferencesCmsContent;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== inViewRef || $[30] !== isFetchingNextPage) {
    t12 = !isFetchingNextPage && <div ref={inViewRef} />;
    $[29] = inViewRef;
    $[30] = isFetchingNextPage;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== showLoaderAtTheEnd) {
    t13 = showLoaderAtTheEnd && <LearnCarouselContentPageSkeleton />;
    $[32] = showLoaderAtTheEnd;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t11 || $[35] !== t12 || $[36] !== t13) {
    t14 = <>{t11}{t12}{t13}</>;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
};
export const LearnCarouselBlockClient = props => {
  const $ = _c(5);
  const [firstRender, setFirstRender] = useState(true);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setFirstRender(false);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const carouselsForCurrentBlock = props.initialGetCarouselsResponse?.guidToCarousels?.[props.currentBlock?.ContentLink?.GuidValue ?? ""] ?? [];
  const queryClient = useQueryClient();
  const carouselsActionData = queryClient.getQueryData(getQueryKeyOfGetCarouselsAction(props.pageBlocks)) as InfiniteData<GetCarouselsActionResponse, unknown>;
  const areSomeCarouselsCached = carouselsActionData?.pages?.length > 0;
  if (!areSomeCarouselsCached && firstRender && carouselsForCurrentBlock.length > 0) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <LearnContentCarouselSkeleton />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  if (!areSomeCarouselsCached && firstRender) {
    return null;
  }
  let t2;
  if ($[3] !== props) {
    t2 = <LearnCarouselBlockClientInner {...props} />;
    $[3] = props;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
function _temp(lastPage) {
  return lastPage.nextCategoriesToSkip;
}
function _temp2(guid, index) {
  return {
    Identifier: guid ?? "",
    Order: index
  };
}
function _temp3(carouselsPage_0) {
  return Object.keys(carouselsPage_0.guidToCarousels);
}